import * as React from "react";
import { Form } from "formsy-react";
import MenuItem from "material-ui/MenuItem";
import Checkbox from "material-ui/Checkbox";
import Divider from "material-ui/Divider";
import Popover, { PopoverAnimationVertical } from "material-ui/Popover";
import IndeterminateCheckBox from "material-ui/svg-icons/toggle/indeterminate-check-box";
import { format } from "lib/globalize-util";
import TextField from "components/form/text-field";
import { SelectWrapper } from "./select-wrapper";

const SELECT_ALL = "selectAll";

const muiStyles = {
    menuItem: {
        paddingLeft: "50px",
    },
    popover: {
        width: "280px",
        height: "250px",
        overflowY: "auto",
    },
    textField: {
        height: "44px",
        display: "block",
    },
    searchWrapper: {
        padding: "10px",
    },
};

const emptySelectedValues = {};

type IOptionValue = string | number;
interface IOption {
    label: string;
    value: IOptionValue;
}

interface IProps {
    label: string;
    showSelectedCount?: boolean;
    options: IOption[];
    errorText?: string;
    dataTest?: string;
    defaultValues?: IOptionValue[];
    onChange?: (selectedValues: IOptionValue[]) => void;
    styles?: { [key: string]: React.CSSProperties };
}

const MultiSelect: React.FC<IProps> = React.memo(function MultiSelectFC(props: IProps) {
    const { defaultValues, label, onChange, options, showSelectedCount, errorText, styles } = props;

    const [selectedValues, setSelectedValues] = React.useState<{ [key: string]: boolean }>(emptySelectedValues);
    const [popoverOpen, setPopoverOpen] = React.useState<boolean>(false);
    const [searchKeyword, setSearchKeyword] = React.useState<string>("");
    const anchorElRef = React.useRef<HTMLDivElement>();
    const itemsStyles = styles?.items ?? {};

    const handleValueSelect = React.useCallback(
        (event) => {
            const { checked } = event.target;
            const value = event.target.getAttribute("data-value");
            let mutatedSelectedValues;
            if (value === SELECT_ALL) {
                if (checked) {
                    mutatedSelectedValues = options.reduce((acc, option) => {
                        acc[option.value] = true;
                        return acc;
                    }, {});
                } else {
                    mutatedSelectedValues = emptySelectedValues;
                }
            } else {
                mutatedSelectedValues = {
                    ...selectedValues,
                    [value]: checked || undefined,
                };
            }

            setSelectedValues(mutatedSelectedValues);
            if (onChange) {
                onChange(
                    Object.keys(mutatedSelectedValues).reduce((acc, valueKey) => {
                        if (mutatedSelectedValues[valueKey]) {
                            acc.push(valueKey);
                        }
                        return acc;
                    }, []),
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedValues, options],
    );

    React.useEffect(() => {
        setSelectedValues(
            options.reduce((acc, option) => {
                acc[option.value] = (defaultValues || []).includes(option.value) ? true : undefined;
                return acc;
            }, {}),
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenToggle = React.useCallback(() => {
        setPopoverOpen((isOpen) => !isOpen);
    }, []);

    const handleRequestClose = React.useCallback(() => {
        setPopoverOpen(false);
    }, []);

    const handleSearchChange = React.useCallback((e, searchTerm) => {
        setSearchKeyword(searchTerm);
    }, []);

    const selectedOptions = React.useMemo(() => {
        return options.filter((option) => selectedValues[option.value]);
    }, [selectedValues, options]);

    const visibleOptions = React.useMemo(() => {
        const matchRegex = new RegExp(searchKeyword, "i");
        return options.filter((option) => matchRegex.test(option.label));
    }, [searchKeyword, options]);

    const areAllSelected = selectedOptions.length && selectedOptions.length === options.length;
    const areSomeSelected = selectedOptions.length > 0 && selectedOptions.length !== options.length;

    const placeholder = React.useMemo(() => {
        if (areAllSelected) {
            return format("All");
        }
        if (!selectedOptions.length) {
            return format("None");
        }
        if (showSelectedCount) {
            return format("|n| selected", { n: selectedOptions.length });
        }
        return selectedOptions.map((option) => option.label).join(", ");
    }, [areAllSelected, selectedOptions, showSelectedCount]);

    const ValueOptions = (
        <Popover
            anchorEl={anchorElRef.current}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            animation={PopoverAnimationVertical}
            onRequestClose={handleRequestClose}
            open={popoverOpen}
            style={muiStyles.popover as any}
            targetOrigin={{ horizontal: "left", vertical: "top" }}
        >
            <Form style={muiStyles.searchWrapper}>
                <TextField
                    className="searchContainer"
                    canClear={true}
                    large={false}
                    name="search"
                    placeholder={format("Search")}
                    autoComplete="off"
                    style={muiStyles.textField}
                    value={searchKeyword}
                    onChange={handleSearchChange}
                />
            </Form>
            <Divider key="divider" />
            <MenuItem
                key={SELECT_ALL}
                value={SELECT_ALL}
                primaryText={format("Select all")}
                leftCheckbox={
                    <Checkbox
                        onCheck={handleValueSelect}
                        data-value={SELECT_ALL}
                        checked={areAllSelected || areSomeSelected}
                        checkedIcon={areSomeSelected ? <IndeterminateCheckBox /> : undefined}
                    />
                }
                style={muiStyles.menuItem}
            />
            {visibleOptions.map((option: any) => {
                return (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        primaryText={option.label}
                        leftCheckbox={
                            <Checkbox
                                onCheck={handleValueSelect}
                                checked={!!selectedValues[option.value]}
                                data-value={option.value}
                            />
                        }
                        style={{ ...muiStyles.menuItem, ...itemsStyles }}
                    />
                );
            })}
        </Popover>
    );

    return (
        <SelectWrapper
            label={label}
            placeholder={placeholder}
            onClick={handleOpenToggle}
            errorText={errorText}
            styles={styles}
        >
            {ValueOptions}
            <div ref={anchorElRef} />
        </SelectWrapper>
    );
});

export { MultiSelect };
